"use client";

import type { NavActionCtaData } from "@homeinstead/shared/site-builder";

import Phone from "@homeinstead/icons/Phone";
import { cn } from "../../../utils/classnames";
import PhoneLink from "../../PhoneLink";
import { NavActionCta } from "../NavActionCta";

type CoBrandedCenterColumnProps = {
  formattedPhone: string;
  phoneNumber: string;
  phoneRowLabel: string;
  /**
   * Secondary CTA — rendered in the center column on large viewports once
   * the visitor has scrolled past the hero. When null, the consultation
   * slot is hidden entirely.
   */
  secondaryCta: NavActionCtaData | null;
  /**
   * Whether the visitor has scrolled past one viewport. Drives the slide-in
   * animation for the secondary CTA — when `scrolledDown && secondaryCta !==
   * null` the CTA fades in, otherwise it slides out and goes `inert`.
   */
  scrolledDown: boolean;
};

function CoBrandedCenterColumn({
  formattedPhone,
  phoneNumber,
  phoneRowLabel,
  scrolledDown,
  secondaryCta,
}: CoBrandedCenterColumnProps) {
  const hasSecondary = secondaryCta !== null;
  const showSecondaryCta = hasSecondary && scrolledDown;
  const ctaWrapperClassName = cn(
    "flex shrink-0 items-center justify-center overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-in-out",
    showSecondaryCta
      ? "pointer-events-auto max-w-[min(100vw,28rem)] translate-x-0 opacity-100 motion-safe:animate-fade-in-right"
      : "pointer-events-none max-w-0 -translate-x-2 opacity-0",
  );

  return (
    <div className="hidden min-w-0 flex-1 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center">
      <div aria-hidden className="min-w-0" />
      <div
        className={cn(
          "flex items-center",
          !hasSecondary || showSecondaryCta ? "gap-5" : "gap-0",
        )}
      >
        <div className="flex max-w-[min(100%,380px)] items-center gap-3">
          <Phone
            aria-hidden
            className="size-4 shrink-0 fill-support-base"
            data-testid="co-brand-phone-icon"
          />

          <p
            className={cn(
              "font-jakarta font-medium text-base text-support-base leading-7",
              !hasSecondary || !showSecondaryCta ? "text-center" : "text-right",
            )}
          >
            <span>{phoneRowLabel} &nbsp;</span>
            <PhoneLink
              className="inline h-fit p-0 text-support-base"
              phoneNumber={phoneNumber}
              variant="plain"
            >
              {formattedPhone}
            </PhoneLink>
          </p>
        </div>

        {hasSecondary ? (
          <div
            className={ctaWrapperClassName}
            inert={!showSecondaryCta ? true : undefined}
          >
            <NavActionCta
              className={cn(
                "inline-flex h-[52px] w-max shrink-0 items-center justify-center rounded-full border-2 border-new-beginnings-base px-6",
              )}
              cta={secondaryCta}
              phoneNumber={phoneNumber}
              variant="friendly"
            />
          </div>
        ) : null}
      </div>
      <div aria-hidden className="min-w-0" />
    </div>
  );
}

export { CoBrandedCenterColumn };
